import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail, STATUS_LABEL } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  Loader2,
  FileText,
  User,
  MapPin,
  Calendar,
  Phone,
  Mail,
  IdCard,
  Home,
  FileDown,
  Clock,
  Paperclip,
} from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatDateOnly(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function FilePreview({ f }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const isImage = f.content_type?.startsWith("image/");

  useEffect(() => {
    let active = true;
    let url;
    (async () => {
      if (!isImage) return;
      try {
        const res = await api.get(`/files/${f.storage_path}`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        if (active) setBlobUrl(url);
      } catch (_e) {
        /* silent */
      }
    })();
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [f.storage_path, isImage]);

  const download = async () => {
    try {
      const res = await api.get(`/files/${f.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error("Gagal mengunduh file");
    }
  };

  return (
    <div
      data-testid={`evidence-file-${f.id}`}
      className="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex flex-col"
    >
      <div className="aspect-video bg-slate-100 flex items-center justify-center">
        {isImage && blobUrl ? (
          <img src={blobUrl} alt={f.original_filename} className="w-full h-full object-cover" />
        ) : isImage ? (
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        ) : (
          <div className="flex flex-col items-center gap-1 text-slate-500">
            <FileText className="h-8 w-8" />
            <div className="text-xs uppercase tracking-wide">
              {f.content_type?.split("/")[1] || "FILE"}
            </div>
          </div>
        )}
      </div>
      <div className="p-3 border-t border-slate-200 bg-white flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-slate-800 truncate">{f.original_filename}</div>
          <div className="text-[11px] text-slate-500">{(f.size / 1024).toFixed(0)} KB</div>
        </div>
        <button
          onClick={download}
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
          data-testid={`download-${f.id}`}
        >
          <FileDown className="h-3 w-3" />
          Unduh
        </button>
      </div>
    </div>
  );
}

export default function AdminReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [catatan, setCatatan] = useState("");
  const [updating, setUpdating] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await api.get(`/reports/${id}`);
      setData(res.data);
      setNewStatus(res.data.status);
      setCatatan(res.data.catatan_admin || "");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Laporan tidak ditemukan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const doUpdate = async () => {
    if (!newStatus) return;
    setUpdating(true);
    try {
      await api.patch(`/reports/${id}/status`, { status: newStatus, catatan: catatan || null });
      toast.success("Status laporan diperbarui");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal memperbarui");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#0B2B5E]" />
      </div>
    );
  }
  if (err) {
    return (
      <div data-testid="detail-error" className="text-sm rounded-md bg-red-50 text-red-700 border border-red-200 px-4 py-3">
        {err}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div data-testid="admin-report-detail" className="space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#0B2B5E]"
        data-testid="back-btn"
      >
        <ArrowLeft className="h-4 w-4" />
        Kembali
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-slate-500">
            Detail Laporan
          </div>
          <div className="font-mono-tight font-black text-3xl text-[#0B2B5E] mt-1">
            {data.nomor_laporan}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Dibuat {formatDate(data.created_at)} • Diperbarui {formatDate(data.updated_at)}
          </div>
        </div>
        <StatusBadge status={data.status} className="text-sm px-3 py-1.5" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Kejadian */}
          <section className="rounded-lg bg-white border border-slate-200 p-6">
            <div className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500 mb-4">
              Informasi Kejadian
            </div>
            <div className="grid sm:grid-cols-2 gap-5 text-sm">
              <InfoItem Icon={FileText} label="Jenis Narkoba" value={data.jenis_narkoba} />
              <InfoItem Icon={Calendar} label="Tanggal Kejadian" value={formatDateOnly(data.tanggal_kejadian)} />
              <InfoItem Icon={MapPin} label="Kabupaten/Kota" value={data.kabupaten_kota} />
              <InfoItem Icon={Home} label="Alamat Kejadian" value={data.alamat_kejadian} />
            </div>
            <div className="mt-5 pt-5 border-t border-slate-200">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                Kronologi Kejadian
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="deskripsi-kejadian">
                {data.deskripsi_kejadian}
              </div>
            </div>
            {data.terlapor_info && (
              <div className="mt-5 pt-5 border-t border-slate-200">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                  Informasi Terlapor
                </div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                  {data.terlapor_info}
                </div>
              </div>
            )}
          </section>

          {/* Pelapor */}
          <section className="rounded-lg bg-white border border-slate-200 p-6">
            <div className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500 mb-4">
              Data Pelapor
            </div>
            {data.pelapor_anonim ? (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                <strong>Laporan anonim.</strong> Pelapor memilih merahasiakan identitas.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5 text-sm">
                <InfoItem Icon={User} label="Nama" value={data.nama_pelapor || "—"} />
                <InfoItem Icon={IdCard} label="NIK" value={data.nik || "—"} mono />
                <InfoItem Icon={Phone} label="Telepon" value={data.no_telepon || "—"} />
                <InfoItem Icon={Mail} label="Email" value={data.email_pelapor || "—"} />
                <div className="sm:col-span-2">
                  <InfoItem Icon={Home} label="Alamat Pelapor" value={data.alamat_pelapor || "—"} />
                </div>
              </div>
            )}
          </section>

          {/* Bukti */}
          <section className="rounded-lg bg-white border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Paperclip className="h-4 w-4 text-[#0B2B5E]" />
              <div className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
                Bukti Pendukung ({data.files?.length || 0})
              </div>
            </div>
            {data.files?.length ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {data.files.map((f) => <FilePreview key={f.id} f={f} />)}
              </div>
            ) : (
              <div className="text-sm text-slate-500">Tidak ada bukti dilampirkan.</div>
            )}
          </section>
        </div>

        {/* RIGHT column */}
        <aside className="space-y-6">
          <section className="rounded-lg bg-white border border-slate-200 p-6">
            <div className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500 mb-4">
              Tindak Lanjut
            </div>
            <label className="text-xs font-semibold text-slate-600">Status Baru</label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="update-status-select" className="h-11 mt-1.5">
                <SelectValue placeholder="Pilih status" />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(STATUS_LABEL).map((k) => (
                  <SelectItem key={k} value={k} data-testid={`update-status-${k}`}>
                    {STATUS_LABEL[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <label className="text-xs font-semibold text-slate-600 block mt-4 mb-1.5">
              Catatan Petugas
            </label>
            <Textarea
              value={catatan}
              onChange={(e) => setCatatan(e.target.value)}
              data-testid="update-catatan"
              placeholder="Catatan tindak lanjut (akan terlihat oleh pelapor)"
              rows={4}
            />

            <button
              onClick={doUpdate}
              disabled={updating}
              data-testid="update-status-btn"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#0B2B5E] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#081f47] transition-colors disabled:opacity-60"
            >
              {updating && <Loader2 className="h-4 w-4 animate-spin" />}
              {updating ? "Menyimpan..." : "Perbarui Status"}
            </button>
          </section>

          <section className="rounded-lg bg-white border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-4 w-4 text-[#0B2B5E]" />
              <div className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
                Riwayat Status
              </div>
            </div>
            <ol className="relative border-l-2 border-slate-200 ml-2 space-y-5">
              {(data.history || []).map((h, i) => (
                <li key={h.id || i} className="ml-5" data-testid={`timeline-item-${i}`}>
                  <div className="absolute -left-[9px] h-4 w-4 rounded-full bg-[#0B2B5E] border-4 border-white" />
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={h.to_status} />
                    <span className="text-[11px] text-slate-500">{formatDate(h.changed_at)}</span>
                  </div>
                  {h.catatan && (
                    <div className="mt-1 text-xs text-slate-700">{h.catatan}</div>
                  )}
                  <div className="mt-1 text-[11px] text-slate-400">
                    Oleh: {h.changed_by}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </aside>
      </div>
    </div>
  );
}

function InfoItem({ Icon, label, value, mono }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className={`mt-1 text-sm text-[#0F172A] ${mono ? "font-mono-tight" : "font-semibold"}`}>
        {value}
      </div>
    </div>
  );
}
